import React, { useEffect, useMemo, useState } from "react";
import Axios from "axios";
import { connect } from "react-redux";
import { Redirect } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Config from "../../../Config";
import { setTime } from "../../../../store/Actions/cartActions";
import { setDTime } from "../../../../store/Actions/authActions";
import {
  apiDate,
  buildDeliveryDays,
  dateKey,
  deliveryConstraints,
  deliveryRestriction,
  firstAvailableSlot,
  parseSlot,
} from "../../../../utils/slotSelection";
import SlotScreen from "./SlotScreen";
import { longDate, shortDate } from "./format";

/**
 * Checkout, étape 3/3 — créneau de livraison.
 *
 * Plancher et appariement dérivent de la collecte choisie (utils/
 * slotSelection) : collecte de journée → +7 jours, journées seulement ;
 * collecte du soir → 48 h, soirées seulement. Le premier créneau
 * disponible est présélectionné, et à chaque changement de jour.
 *
 * Pour une adresse à domicile, le serveur valide le couple collecte /
 * livraison avant qu'on continue ; son message d'erreur est affiché tel
 * quel. Un point de collecte n'est pas validé (règle de l'app mobile).
 */
const DeliverySlot = ({
  history,
  dispatch,
  collectionTimes,
  hasCart,
  hasAddress,
  isHome,
  pickup,
  pickupRaw,
  savedDelivery,
  savedSlot,
  authUser,
  editing,
  now,
}) => {
  const { t } = useTranslation();

  const pickupKey = dateKey(pickup?.date?.date);
  const pickupSlot = parseSlot(pickupRaw);

  const days = useMemo(
    () =>
      pickupKey && pickupSlot
        ? buildDeliveryDays({
            collectionTimes,
            pickupDateKey: pickupKey,
            pickupSlot,
            now: now || new Date(),
          })
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [collectionTimes, pickupKey, pickupRaw, now]
  );

  const savedKey = dateKey(savedDelivery?.date?.date);
  const initialDay =
    days.find((day) => day.key === savedKey) || days[0] || null;

  const [dayKey, setDayKey] = useState(initialDay?.key || null);
  const [slotRaw, setSlotRaw] = useState(() => {
    const saved = initialDay?.slots.find(
      (slot) => slot.raw === savedSlot && !slot.disabled
    );

    return (saved || firstAvailableSlot(initialDay))?.raw || null;
  });
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (days.length && !days.some((day) => day.key === dayKey)) {
      setDayKey(days[0].key);
      setSlotRaw(firstAvailableSlot(days[0])?.raw || null);
    }
  }, [days, dayKey]);

  if (!hasCart || !hasAddress) {
    return <Redirect to="/home" />;
  }

  /* Sans collecte choisie, pas de plancher possible : retour à l'étape 2. */
  if (!pickupKey || !pickupSlot) {
    return <Redirect to="/checkout/pickup" />;
  }

  const { allow } = deliveryConstraints({
    pickupDateKey: pickupKey,
    pickupSlot,
  });
  const day = days.find((item) => item.key === dayKey) || null;
  const slot =
    day?.slots.find((item) => item.raw === slotRaw && !item.disabled) || null;
  const restriction = deliveryRestriction(day, allow);

  const infos = [];

  if (restriction?.kind === "daytime") {
    infos.push({
      icon: "info",
      text: `${t(
        "For logistical reasons, orders collected during the day are delivered during the day. First possible delivery:"
      )} ${days[0] ? longDate(days[0].key) : "—"}.`,
    });
  }

  if (restriction?.kind === "evening") {
    infos.push({
      icon: "info",
      text: t(
        "For logistical reasons, orders collected in the evening are delivered only in the evening."
      ),
    });
  }

  const pickupSummary = `${shortDate(pickupKey)} · ${pickupSlot.label}`;
  const deliverySummary = slot ? `${shortDate(day.key)} · ${slot.label}` : null;

  const saveAndContinue = () => {
    dispatch(setTime({ date: { day: day.day, date: day.key } }));
    dispatch(setDTime(slot.raw));
    history.push("/checkout");
  };

  const validateAndContinue = async () => {
    setError(null);

    if (!isHome) {
      saveAndContinue();
      return;
    }

    setChecking(true);

    try {
      const response = await Axios({
        url: `${Config.apiURL}/customer/validate_delivery_date`,
        method: "POST",
        headers: {
          uid: `${authUser?.user_uid}`,
          client: `${authUser?.user_client}`,
          "access-token": `${authUser?.user_access_token}`,
        },
        data: {
          pickup_date: apiDate({ day: pickup.date.day, key: pickupKey }),
          pickup_slot: pickupSlot.raw,
          delivery_date: apiDate(day),
          delivery_slot: slot.raw,
        },
      });

      if (response?.data?.message === "Valid") {
        saveAndContinue();
        return;
      }

      setChecking(false);
      setError(response?.data?.message || t("This slot cannot be selected."));
    } catch (err) {
      setChecking(false);
      setError(
        err?.response?.data?.message ||
          t("Server is not responding at the moment, please try later")
      );
    }
  };

  return (
    <SlotScreen
      t={t}
      step={{ done: 3, total: 3, label: `${t("Step")} 3 / 3` }}
      onBack={() => history.push(editing ? "/checkout" : "/checkout/pickup")}
      title={t("Delivery slot")}
      lede={t("When should we bring your laundry back?")}
      days={days}
      dayKey={dayKey}
      onSelectDay={(key) => {
        setDayKey(key);
        setError(null);
        setSlotRaw(
          firstAvailableSlot(days.find((item) => item.key === key))?.raw ||
            null
        );
      }}
      slotRaw={slotRaw}
      onSelectSlot={(picked) => {
        setError(null);
        setSlotRaw(picked.raw);
      }}
      slotsLabel={t("Delivery slots")}
      infos={infos}
      error={error}
      pickupSummary={pickupSummary}
      deliverySummary={deliverySummary}
      ctaLabel={
        checking
          ? t("processing")
          : slot
          ? `${t("Continue")} · ${shortDate(day.key).split(" ")[0]} ${slot.label}`
          : t("Continue")
      }
      ctaDisabled={!slot || checking}
      onContinue={validateAndContinue}
    />
  );
};

export { DeliverySlot };

const mapStateToProps = (state) => ({
  collectionTimes: state?.auth?.selectedAddress?.[0]?.collection_times || [],
  hasAddress: Boolean(state?.auth?.selectedAddress?.[0]),
  hasCart: (state?.cart?.cartItems || []).length > 0,
  /* cart.home : adresse à domicile (true) ou point de collecte. Même lecture
     que ConfirmOrder, qui envoie « CollectionPoint » dès qu'il n'est pas vrai. */
  isHome: state?.cart?.home === true,
  pickup: state?.cart?.pickup,
  pickupRaw: state?.auth?.pickupTime,
  savedDelivery: state?.cart?.time,
  savedSlot: state?.auth?.deliveryTime,
  authUser: state?.auth?.user,
  /* En modification de commande, on revient au récapitulatif d'édition. */
  editing: Boolean(state?.auth?.editContext),
});

export default connect(mapStateToProps)(DeliverySlot);
